import { supabase } from '../supabaseClient';

const CACHE_TIMEOUT_MS = 5000;
const lastLogged = new Map<string, number>();

/**
 * Logs user activity to the server.
 * Includes client-side debounce to prevent unintentional spam before it hits the API.
 * 
 * @param featureName A descriptive name of the feature being accessed or interacted with (e.g. 'Mentor Click', 'Tryout Exam Accessed')
 */
export async function trackUserActivity(featureName: string): Promise<void> {
    try {
        const route = window.location.pathname;
        const dedupeKey = `${featureName}_${route}`;
        const now = Date.now();
        const lastSeen = lastLogged.get(dedupeKey);

        if (lastSeen && (now - lastSeen) < CACHE_TIMEOUT_MS) {
            // Silently ignore duplicate calls within 5s
            return;
        }
        lastLogged.set(dedupeKey, now);

        // Keep the map from growing indefinitely
        if (lastLogged.size > 200) {
            lastLogged.clear();
        }

        // Get the current session if it exists to attach to the header
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        const username = session?.user?.user_metadata?.full_name || session?.user?.email || undefined;

        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        await fetch('/api/log-activity', {
            method: 'POST',
            headers,
            body: JSON.stringify({
                feature_name: featureName,
                route,
                username // Pass username directly if available in the frontend session
            })
        });

    } catch (error) {
        // We log silently since we don't want telemetry errors to break user experience
        console.warn('[UserActivityLogger] Failed to send activity log', error);
    }
}
